// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import borderByState, { sizeVariantValues } from "@insite/mobius/FormField/formStyles";
import { IconWrapper } from "@insite/mobius/Icon";
import get from "@insite/mobius/utilities/get";
import getProp from "@insite/mobius/utilities/getProp";
import resolveColor from "@insite/mobius/utilities/resolveColor";
import { xBaseTheme } from "@mcmc/XDL/types";
import Color from "color";
import { css } from "styled-components";

sizeVariantValues.default = {
    fontSize: 16,
    height: 40,
    icon: 16,
    iconPadding: 10,
    descriptionLineSpacing: 5,
    borderRadius: 7,
    datePickerWidth: 280,
    leftLabelPadding: 9,
    button: "large",
    labelHeight: 21,
};

sizeVariantValues.small = {
    fontSize: 14,
    height: 35,
    icon: 16,
    iconPadding: 10,
    descriptionLineSpacing: 5,
    borderRadius: 7,
    datePickerWidth: 280,
    leftLabelPadding: 9,
    button: "medium",
    labelHeight: 21,
};

const iconPadding = css`
    padding-right: 40px;
    &:last-child {
        padding-right: 10px;
    }
`;

const styleInputs = (props: any) => {
    return css`
        ${iconPadding}
        ${
            props.error &&
            css`
                &:not(:disabled) {
                    ${borderByState(props, "inactive")}
                    padding: 0 10px 0 7px;
                    border-left: 4px solid ${resolveColor("danger", props.theme)};
                    & ~ ${IconWrapper as any} .search-icon {
                        color: ${resolveColor("danger", props.theme)};
                    }
                    ${iconPadding}
                }
            `
        }
        &:disabled {
            ${borderByState(props, "inactive")}
            background: ${resolveColor("common.background", props.theme)};
            color: ${resolveColor("text.main", props.theme)};
            opacity:0.5; 

            ${IconWrapper as any}, & ~ button ${IconWrapper as any}, & ~ ${IconWrapper as any}{
                background: transparent;
                opacity:0.5; 
            }

 
            ${iconPadding}
        }
        
        &:not(:disabled) {
            &:focus, .focus-within,&:focus-within {
                ${borderByState(props, "inactive")}
                box-shadow: ${get(props.theme, "shadows.1")};
                ${iconPadding}
            }
            &:active, &.react-datetime-picker--open {
                ${borderByState(props, "inactive")}
                outline-color: ${getProp("theme.colors.common.border", "#09f")};
                outline-style: ${getProp("theme.focus.style", "solid")};
                outline-width: ${getProp("theme.focus.width", "2px")};
                outline-offset: -${getProp("theme.focus.width", "2px")};
                box-shadow: none;

                ${IconWrapper as any}, & ~ button ${IconWrapper as any}, & ~ ${IconWrapper as any}{
                    color:${resolveColor("primary", props.theme)};
                }
                ${iconPadding}
            }
        }                                                                                                                   
    `;
};

export default {
    defaultProps: {
        border: "rectangle",
        sizeVariant: "small",
        cssOverrides: {
            formField: css`
                line-height: 0;
                ${(props: any) => {
                    if (props.disabled) {
                        return `label{color:${Color(resolveColor("text", props.theme)).rgb().alpha(0.5)}}`;
                    }

                    if (props.error) {
                        return `
                        label{color:${resolveColor("danger", props.theme)}};
                        textarea:not(:disabled) {
                          padding-left:7px;
                        }
                        `;
                    }
                }}

                textarea {
                    min-height: 60px;
                    max-width: 100%;
                    letter-spacing: 0.2px;
                    width: 100% !important /*Override Inline style on resize*/;
                    ${styleInputs}
                    &:focus-within, &:not(:disabled), &:disabled, &:focus:not(:disabled), &:active:not(:disabled) {
                        padding-top: 8px;
                        padding-bottom: 8px;
                    }
                }

                // We also have "select" style in globalStyle file
            `,

            inputSelect: css`
                border-width: 1px;
                letter-spacing: 0.2px;
                line-height: 15px;
                word-break: initial;
                overflow-wrap: initial;
                ${styleInputs}
                &.react-datetime-picker {
                    height: ${({ _sizeVariant }: any) => (sizeVariantValues as any)[_sizeVariant].height}px;
                    &,
                    &:focus,
                    &:active,
                    &.react-datetime-picker--enabled {
                        padding-right: 0 !important;
                    }
                }
            `,
        },
        errorProps: {
            size: "12px",
            lineHeight: "14px",
            weight: "normal",
            css: css`
                &:first-letter {
                    text-transform: uppercase;
                }
            `,
        },
        hintProps: {
            size: "12px",
            lineHeight: "14px",
            weight: "normal",
            color: "common.ui1",
            css: css`
                &:first-letter {
                    text-transform: uppercase;
                }
            `,
        },
        labelProps: {
            lineHeight: "16px",
            css: css`
                min-height: 0;
                width: auto;
            `,
        },
        iconProps: {
            color: "secondary",
        },
    },
} as xBaseTheme["formField"];
